/**
 * Shared media preview for a Zernio post, used by both the calendar chips and
 * the post list. The URLs come from the post's own `mediaItems` — the media
 * uploaded when the post was created — so nothing is re-uploaded or copied for
 * display purposes.
 *
 * A VIDEO WITH A CUSTOM COVER SHOWS THE COVER. Without that, this listed a
 * frame the browser happened to decode while Instagram showed the chosen cover
 * — the same post looking like two different posts depending where you saw it,
 * with nothing to explain why.
 */
import { Play, Image as ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ZernioPost } from '@/services/zernio.service';

export function primaryMedia(post: ZernioPost) {
  return post.mediaItems?.find((m) => m.url) ?? null;
}

interface Props {
  post: ZernioPost;
  className?: string;
  iconClassName?: string;
}

export function SocialPostThumb({ post, className, iconClassName }: Props) {
  const media = primaryMedia(post);

  if (!media?.url) {
    return (
      <div className={cn('grid place-items-center bg-slate-100 text-slate-400', className)}>
        <ImageIcon className={cn('h-3 w-3', iconClassName)} aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className={cn('relative overflow-hidden bg-slate-100', className)}>
      {media.type === 'video' ? (
        <>
          {media.thumbnail ? (
            <img src={media.thumbnail} alt="" loading="lazy" className="h-full w-full object-cover" />
          ) : (
            <video src={media.url} muted playsInline preload="metadata" className="h-full w-full object-cover" />
          )}
          <span className="absolute inset-0 grid place-items-center">
            <Play className={cn('h-3 w-3 fill-white text-white drop-shadow', iconClassName)} aria-hidden="true" />
          </span>
        </>
      ) : (
        <img src={media.url} alt="" loading="lazy" className="h-full w-full object-cover" />
      )}
    </div>
  );
}
